import React from 'react';
import { BellIcon, ClipboardListIcon, UsersIcon, CalendarIcon } from '../icons';
import { formatDateSafe } from '../../utils/helpers';

const AdminNotificationsCenter = ({
  notifications,
  onMarkNotificationRead,
  onResolveNotification,
  onMarkAllNotificationsRead,
  onNav,
}) => {
  const unreadCount = notifications.filter((item) => item.status === 'unread').length;
  const resolvedCount = notifications.filter((item) => item.status === 'resolved').length;
  const activeNotifications = notifications.filter((item) => item.status !== 'resolved');
  const typeStyles = {
    registration_request: {
      label: 'Registro', icon: UsersIcon, accent: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', targetLabel: 'Revisar registro',
    },
    submission: {
      label: 'Envío',
      icon: ClipboardListIcon,
      accent: 'bg-[#004AAD]',
      bg: 'bg-[#004AAD]/8',
      text: 'text-[#004AAD]',
      targetLabel: 'Revisar envíos',
    },
    delegate_request: {
      label: 'Delegado',
      icon: UsersIcon,
      accent: 'bg-amber-400',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      targetLabel: 'Ver usuarios',
    },
    extension_request: {
      label: 'Prórroga',
      icon: CalendarIcon,
      accent: 'bg-emerald-500',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      targetLabel: 'Gestionar plazos',
    },
  };

  const handleOpenTarget = (item) => {
    if (item.status === 'unread') {
      onMarkNotificationRead(item.id);
    }
    if (item.targetPage) {
      onNav(item.targetPage);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200/70 bg-white p-5 shadow-sm md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[0.6rem] uppercase text-slate-400 mb-0.5">Centro de notificaciones</p>
            <h2 className="text-lg font-semibold text-slate-900">Mensajes operativos de empresas</h2>
            <p className="text-sm text-slate-500 mt-1">
              Solicitudes y eventos que requieren seguimiento del equipo administrador.
            </p>
          </div>
          <button
            type="button"
            onClick={onMarkAllNotificationsRead}
            disabled={unreadCount === 0}
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45 md:w-auto"
          >
            Marcar todo como leído
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Sin leer', value: unreadCount, helper: 'requieren atención', accent: 'bg-[#004AAD]' },
            { label: 'Abiertas', value: activeNotifications.length, helper: 'en seguimiento', accent: 'bg-amber-400' },
            { label: 'Resueltas', value: resolvedCount, helper: 'cerradas por admin', accent: 'bg-emerald-500' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-slate-200/70 bg-slate-50/60 p-4">
              <div className={`mb-3 h-1 w-6 rounded-full ${stat.accent}`} />
              <p className="text-[0.6rem] uppercase text-slate-400">{stat.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
              <p className="text-xs text-slate-500">{stat.helper}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 md:px-6">
          <h3 className="text-lg font-semibold text-slate-900">Bandeja administrativa</h3>
          <span className="text-xs text-slate-500">
            {notifications.length ? `${notifications.length} notificaciones` : 'Sin notificaciones'}
          </span>
        </div>
        {notifications.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
              <BellIcon />
            </div>
            <p className="text-sm font-semibold text-slate-800">Aún no hay solicitudes</p>
            <p className="mt-1 text-sm text-slate-500">
              Cuando una empresa envíe un cuestionario o solicite apoyo, aparecerá aquí.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {notifications.map((item) => {
              const style = typeStyles[item.type] || typeStyles.submission;
              const Icon = style.icon;
              const isUnread = item.status === 'unread';
              const isResolved = item.status === 'resolved';
              return (
                <div key={item.id} className={`p-5 transition-colors md:p-6 ${isUnread ? 'bg-[#004AAD]/[0.03]' : 'bg-white'}`}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${style.bg} ${style.text}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${style.accent}`} />
                        <span className="text-[0.65rem] font-semibold uppercase text-slate-400">{style.label}</span>
                        {isUnread && (
                          <span className="rounded-full bg-[#004AAD]/10 px-2 py-0.5 text-[0.65rem] font-semibold text-[#004AAD]">
                            Nuevo
                          </span>
                        )}
                        {isResolved && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-700">
                            Resuelto
                          </span>
                        )}
                      </div>
                      <h4 className="mt-2 text-base font-semibold text-slate-900">{item.title}</h4>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.message}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                          {item.companyName || 'Empresa no identificada'}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                          {formatDateSafe(item.createdAt, 'Sin fecha', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
                      {item.targetPage && (
                        <button
                          type="button"
                          onClick={() => handleOpenTarget(item)}
                          className="rounded-xl bg-[#004AAD] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#003B8A]"
                        >
                          {style.targetLabel}
                        </button>
                      )}
                      {isUnread && (
                        <button
                          type="button"
                          onClick={() => onMarkNotificationRead(item.id)}
                          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
                        >
                          Marcar leída
                        </button>
                      )}
                      {!isResolved && (
                        <button
                          type="button"
                          onClick={() => onResolveNotification(item.id)}
                          className="rounded-xl border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                        >
                          Resolver
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationsCenter;
